import { jsx as _jsx } from "react/jsx-runtime";
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { handleKeyDownInput } from '#pplx/frontend/composer-sdk/internal/keyboard.js';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { COMMAND_PRIORITY_LOW, KEY_ENTER_COMMAND } from 'lexical';
import { useEffect, useRef } from 'react';
/**
 * Mounts inside `LexicalComposer` and owns the keyboard submit gestures.
 * Compact composers always submit on plain Enter; expanded composers follow
 * the surface's `enterKeyBehavior` policy. Newline gestures fall through to
 * the editor when allowed by the surface. `onSubmit` receives the minimal
 * key info for the gesture.
 */
export const SubmitPlugin = ({ compact, compactShiftEnterBehavior, enterKeyBehavior, isDisabled, onSubmit, }) => {
    return compact ? (_jsx(CompactEnterSubmit, { compactShiftEnterBehavior: compactShiftEnterBehavior, isDisabled: isDisabled, onSubmit: onSubmit })) : (_jsx(ExpandedEnterSubmit, { enterKeyBehavior: enterKeyBehavior, isDisabled: isDisabled, onSubmit: onSubmit }));
};
// Compact composers submit the current draft on plain Enter, so that gesture
// must suppress the paragraph insertion from the editor's rich-text handling.
// Shift+Enter follows the surface's explicit compact policy.
//
// A highlighted typeahead row — for example the currently selected
// slash-command suggestion — still wins the keystroke first: pressing
// Enter accepts that suggestion instead of submitting the whole draft.
// When nothing is highlighted the compact handler runs and Enter submits,
// matching the send-button click.
//
// Implementation: register at `COMMAND_PRIORITY_LOW` so this handler runs
// before rich-text's editor-priority newline insertion and after
// typeahead's normal-priority Enter handler. A plain DOM `keydown` listener
// cannot achieve this ordering — Lexical's own root listener dispatches
// `KEY_ENTER_COMMAND` before any React-registered DOM listener runs, so
// rich-text calls `preventDefault()` first and an "ignore defaultPrevented"
// DOM listener has already lost the race.
const CompactEnterSubmit = ({ compactShiftEnterBehavior, isDisabled, onSubmit, }) => {
    const [editor] = useLexicalComposerContext();
    const isDisabledRef = useRef(isDisabled);
    const isComposingRef = useRef(false);
    const onSubmitRef = useRef(onSubmit);
    isDisabledRef.current = isDisabled;
    onSubmitRef.current = onSubmit;
    useEffect(() => {
        const handleCompositionStart = () => {
            isComposingRef.current = true;
        };
        const handleCompositionEnd = () => {
            isComposingRef.current = false;
        };
        const removeCompositionListeners = editor.registerRootListener((rootEl, prevEl) => {
            rootEl?.addEventListener('compositionstart', handleCompositionStart);
            rootEl?.addEventListener('compositionend', handleCompositionEnd);
            prevEl?.removeEventListener('compositionstart', handleCompositionStart);
            prevEl?.removeEventListener('compositionend', handleCompositionEnd);
        });
        const removeEnterCommand = editor.registerCommand(KEY_ENTER_COMMAND, (event) => {
            // Button-driven synthetic dispatches carry no event; leave them alone.
            if (event === null) {
                return false;
            }
            // Let composition finish naturally — no submit — but keep the newline
            // blocked so the caret doesn't drop to the next line mid-composition.
            if (isComposingRef.current ||
                event.isComposing ||
                event.keyCode === 229 ||
                editor.isComposing()) {
                event.preventDefault();
                return true;
            }
            if (event.shiftKey) {
                switch (compactShiftEnterBehavior) {
                    case 'newline':
                        return false;
                    case 'ignore':
                        event.preventDefault();
                        return true;
                    default:
                        return assertUnreachableValue(compactShiftEnterBehavior);
                }
            }
            event.preventDefault();
            if (isDisabledRef.current) {
                return true;
            }
            onSubmitRef.current({
                key: event.key,
                metaKey: event.metaKey,
                ctrlKey: event.ctrlKey,
            });
            return true;
        }, COMMAND_PRIORITY_LOW);
        return () => {
            removeEnterCommand();
            removeCompositionListeners();
        };
    }, [editor, compactShiftEnterBehavior]);
    return null;
};
// Expanded mode receives Enter through Lexical's command pipeline and
// defers newline / IME / disabled logic to `handleKeyDownInput`.
const ExpandedEnterSubmit = ({ enterKeyBehavior, isDisabled, onSubmit, }) => {
    const [editor] = useLexicalComposerContext();
    useEffect(() => {
        return editor.registerCommand(KEY_ENTER_COMMAND, (event) => {
            if (event === null || event.shiftKey) {
                return false;
            }
            handleKeyDownInput(event, {
                enterKeyBehavior,
                isComposing: event.isComposing,
                onKeyDown: () => {
                    if (!isDisabled) {
                        onSubmit({ key: event.key, metaKey: event.metaKey, ctrlKey: event.ctrlKey });
                    }
                },
            });
            const handled = event.defaultPrevented;
            // Reporting an Enter as unhandled (the 'newline' policy) lets the
            // editor insert a newline, exactly like Shift+Enter.
            return handled;
        }, 
        // Higher-priority consumers such as a highlighted typeahead row win first.
        COMMAND_PRIORITY_LOW);
    }, [editor, enterKeyBehavior, isDisabled, onSubmit]);
    return null;
};
//# sourceMappingURL=SubmitPlugin.js.map